import axios, { AxiosInstance } from 'axios'
import type {
  ApiResponse,
  AuthResponse,
  AdminUser,
  Shop,
  ShopUsage,
  ShopUser,
  ShopBranch,
  Plan,
  Subscription,
  BillingInvoice,
  MoneyLog,
  FeatureFlag,
  FeatureFlagOverride,
  MenuConfig,
  SupportTicket,
  TicketMessage,
  AuditLog,
  DashboardSummary,
  NotificationTemplate,
  NotificationLog,
  SystemSettings,
  PaginatedResponse,
  MenuVisibility,
  BusinessType,
  PaymentMethod,
  PaymentMethodType,
  SubscriptionPromotion,
  Referrer,
  ReferrerShop,
  ReferralCommission,
  ConsentDocument,
  Announcement,
  PermissionResource,
  AdminRoleInfo,
  UserPermissions,
} from '../types'

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/admin',
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('queza_admin_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from the sign-in endpoints means "wrong password / code" — let the login page show it
    // instead of reloading (which wiped the error message).
    const isLoginCall = /\/auth\/login/.test(String(error.config?.url ?? ''))
    if (error.response?.status === 401 && !isLoginCall) {
      localStorage.removeItem('queza_admin_token')
      localStorage.removeItem('queza_admin_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default api

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    api.post<ApiResponse<LoginResult>>('/auth/login', { email, password }),
  loginTwoFactor: (challengeToken: string, code: string) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login/2fa', { challengeToken, code }),
  loginTwoFactorSetup: (challengeToken: string) =>
    api.post<ApiResponse<TotpEnrollment>>('/auth/login/2fa/setup', { challengeToken }),
  loginTwoFactorEnable: (challengeToken: string, code: string) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login/2fa/enable', { challengeToken, code }),
  me: () => api.get<ApiResponse<AdminUser>>('/auth/me'),
  logout: () => api.post('/auth/logout'),
  twoFactorStatus: () => api.get<ApiResponse<{ enabled: boolean; enabledAt: string | null; required: boolean }>>('/auth/2fa'),
  twoFactorSetup: () => api.post<ApiResponse<TotpEnrollment>>('/auth/2fa/setup'),
  twoFactorEnable: (code: string) => api.post<ApiResponse<{ enabled: boolean }>>('/auth/2fa/enable', { code }),
  twoFactorDisable: (code: string) => api.post<ApiResponse<{ enabled: boolean }>>('/auth/2fa/disable', { code }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<ApiResponse<null>>('/auth/password', { currentPassword, newPassword }),
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const dashboardApi = {
  getSummary: () => api.get<ApiResponse<DashboardSummary>>('/dashboard/summary'),
}

// ─── Shops ────────────────────────────────────────────────────────────────────
export interface ShopsListParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  plan?: string
  expiringDays?: number
  inactiveDays?: number
  sort?: 'created' | 'name' | 'nextBilling' | 'lastActivity' | 'bookings30d'
}

export type ShopsMapPin = { branchId: string; branchName: string; latitude: number; longitude: number; isMain: boolean; address: string | null; phone: string | null; branchStatus: string; shopId: string; shopName: string; shopStatus: string; planCode: string | null; subStatus: string | null }
export type ShopsMapData = { pins: ShopsMapPin[]; totals: { shops: number; pinned: number }; unpinned: { id: string; name: string; status: string }[] }

export const shopsApi = {
  /** Downloads the filtered list as CSV (UTF-8 with BOM, opens in Excel). */
  exportCsv: async (params: ShopsListParams = {}) => {
    const res = await api.get<Blob>('/shops/export.csv', { params, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `queza-shops-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  },
  getUsers: (id: string) => api.get<ApiResponse<ShopUser[]>>(`/shops/${id}/users`),
  verifyUserEmail: (id: string, userId: string) =>
    api.post<ApiResponse<{ id: string; emailVerifiedAt: string }>>(`/shops/${id}/users/${userId}/verify-email`),
  sendUserPasswordReset: (id: string, userId: string) =>
    api.post<ApiResponse<{ id: string; email: string }>>(`/shops/${id}/users/${userId}/send-password-reset`),
  getBranches: (id: string) => api.get<ApiResponse<ShopBranch[]>>(`/shops/${id}/branches`),
  /** Branch pins for the map page (+ shops without a pin). */
  map: () => api.get<{ data: ShopsMapData }>('/shops/map').then((r) => r.data.data),
  list: (params: ShopsListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<Shop>>>('/shops', { params }),
  get: (id: string) => api.get<ApiResponse<Shop>>(`/shops/${id}`),
  getUsage: (id: string) => api.get<ApiResponse<ShopUsage>>(`/shops/${id}/usage`),
  suspend: (id: string, reason: string) =>
    api.post<ApiResponse<Shop>>(`/shops/${id}/suspend`, { reason }),
  reactivate: (id: string, reason: string) =>
    api.post<ApiResponse<Shop>>(`/shops/${id}/reactivate`, { reason }),
  update: (id: string, data: {
    businessTypeId?: string | null
    logoUrl?: string | null
    timezoneOverride?: string | null
    internalNote?: string | null
  }) => api.patch<ApiResponse<Shop>>(`/shops/${id}`, data),
  getAuditLogs: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<PaginatedResponse<AuditLog>>>(`/shops/${id}/audit-logs`, { params }),
  getNotifications: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<PaginatedResponse<NotificationLog>>>(`/shops/${id}/notifications`, { params }),
  getTickets: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<PaginatedResponse<SupportTicket>>>(`/shops/${id}/tickets`, { params }),
  getInvoices: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<PaginatedResponse<BillingInvoice>>>(`/shops/${id}/invoices`, { params }),
  getFeatureFlags: (id: string) =>
    api.get<ApiResponse<FeatureFlag[]>>(`/shops/${id}/feature-flags`),
  getMenuConfig: (id: string) =>
    api.get<ApiResponse<MenuConfig>>(`/shops/${id}/menu-config`),
}

// ─── Plans ────────────────────────────────────────────────────────────────────
export interface PlanPayload {
  displayName: string
  description: string
  priceMonthly: number
  priceYearly: number
  isActive: boolean
  branchLimit: number
  staffLimit: number
  bookingLimit: number | null
  customerLimit: number | null
  hasPOS: boolean
  hasCRM: boolean
  hasMemberships: boolean
  hasReports: boolean
  hasMultiBranch: boolean
  hasAICenter: boolean
  hasNotificationAutomation: boolean
}

export const plansApi = {
  list: () => api.get<ApiResponse<Plan[]>>('/plans'),
  get: (id: string) => api.get<ApiResponse<Plan>>(`/plans/${id}`),
  create: (data: PlanPayload) => api.post<ApiResponse<Plan>>('/plans', data),
  update: (id: string, data: Partial<PlanPayload>) =>
    api.patch<ApiResponse<Plan>>(`/plans/${id}`, data),
  schedulePrice: (id: string, data: { priceMonthly: number; priceYearly: number; effectiveAt?: string }) =>
    api.post<ApiResponse<Plan>>(`/plans/${id}/prices`, data),
}

export interface SubscriptionPromotionPayload {
  code: string; name: string; status: 'draft' | 'active' | 'paused'; discountType: 'percent' | 'fixed'; discountValue: number
  maxDiscountSatang?: number | null; startsAt: string; endsAt?: string | null; totalQuota?: number | null
  allowedPlanIds: string[]; allowedBillingCycles: ('monthly' | 'yearly')[]
  benefitCycles?: number; autoApply?: boolean
}
export const subscriptionPromotionsApi = {
  list: () => api.get<ApiResponse<SubscriptionPromotion[]>>('/subscription-promotions'),
  create: (data: SubscriptionPromotionPayload) => api.post<ApiResponse<SubscriptionPromotion>>('/subscription-promotions', data),
  update: (id: string, data: Partial<SubscriptionPromotionPayload>) => api.patch<ApiResponse<SubscriptionPromotion>>(`/subscription-promotions/${id}`, data),
  setStatus: (id: string, status: 'draft' | 'active' | 'paused') => api.patch<ApiResponse<SubscriptionPromotion>>(`/subscription-promotions/${id}/status`, { status }),
  redemptions: (id: string) => api.get(`/subscription-promotions/${id}/redemptions`),
}

export interface ReferrerPayload {
  type: 'sales' | 'affiliate'; code: string; name: string; phone?: string | null; email?: string | null
  commissionPercent: number; commissionMonths: number; bonusStarterSatang: number; bonusProSatang: number; bonusBusinessSatang: number
  status: 'active' | 'inactive'; note?: string | null
  bankName?: string | null; bankAccountName?: string | null; bankAccountNumber?: string | null
}
export const referrersApi = {
  list: () => api.get<ApiResponse<Referrer[]>>('/referrers'),
  create: (data: ReferrerPayload) => api.post<ApiResponse<Referrer>>('/referrers', data),
  update: (id: string, data: Partial<ReferrerPayload>) => api.patch<ApiResponse<Referrer>>(`/referrers/${id}`, data),
  shops: (id: string) => api.get<ApiResponse<ReferrerShop[]>>(`/referrers/${id}/shops`),
  attachShop: (id: string, shopId: string) => api.post<ApiResponse<ReferrerShop>>(`/referrers/${id}/shops`, { shopId }),
  detachShop: (id: string, shopId: string) => api.delete<ApiResponse<{ shopId: string }>>(`/referrers/${id}/shops/${shopId}`),
  commissions: (params: { period?: string; status?: string; referrerId?: string }) =>
    api.get<ApiResponse<{ items: ReferralCommission[]; totals: { pendingSatang: number; payableSatang: number; paidSatang: number }; minPayoutSatang: number }>>('/referrers/commissions', { params }),
  pay: (ids: string[]) => api.post<ApiResponse<{ updated: number; onHold: number; skipped: { referrerId: string; name: string; reason: string }[] }>>('/referrers/commissions/pay', { ids }),
  approval: (id: string, status: 'approved' | 'rejected' | 'pending', note?: string | null) => api.post<ApiResponse<Referrer>>(`/referrers/${id}/approval`, { status, note: note ?? null }),
  verify: (id: string, what: 'email' | 'phone') => api.post<ApiResponse<Referrer>>(`/referrers/${id}/verify`, { what }),
}

export interface ConsentPayload {
  audience: 'shop' | 'affiliate'; key: string; title: string; summary?: string | null; body: string
  required: boolean; isActive: boolean; sortOrder: number
}
export const consentsApi = {
  list: (audience: 'shop' | 'affiliate') => api.get<ApiResponse<ConsentDocument[]>>('/consents', { params: { audience } }),
  create: (data: ConsentPayload) => api.post<ApiResponse<ConsentDocument>>('/consents', data),
  update: (id: string, data: Partial<Omit<ConsentPayload, 'audience' | 'key'>> & { newVersion?: boolean }) => api.patch<ApiResponse<ConsentDocument>>(`/consents/${id}`, data),
}

// ─── Subscriptions ────────────────────────────────────────────────────────────
export interface SubscriptionsListParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  planId?: string
}

export const subscriptionsApi = {
  list: (params: SubscriptionsListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<Subscription>>>('/subscriptions', { params }),
  get: (id: string) => api.get<ApiResponse<Subscription>>(`/subscriptions/${id}`),
  changePlan: (id: string, planId: string, reason: string) =>
    api.post<ApiResponse<Subscription>>(`/subscriptions/${id}/change-plan`, { planId, reason }),
  extend: (id: string, days: number, reason: string) =>
    api.post<ApiResponse<Subscription>>(`/subscriptions/${id}/extend`, { days, reason }),
  cancel: (id: string, reason: string) =>
    api.post<ApiResponse<Subscription>>(`/subscriptions/${id}/cancel`, { reason }),
  reactivate: (id: string, reason: string) =>
    api.post<ApiResponse<Subscription>>(`/subscriptions/${id}/reactivate`, { reason }),
  setAddons: (id: string, addons: string[], months: number, reason: string) =>
    api.post<ApiResponse<Subscription>>(`/subscriptions/${id}/addons`, { addons, months, reason }),
}

// ─── Billing ──────────────────────────────────────────────────────────────────
export interface BillingListParams {
  page?: number
  limit?: number
  search?: string
  status?: string
}

export interface ManualPaymentPayload {
  shopId: string
  invoiceId?: string
  amount: number
  method: string
  reference: string
  reason: string
}

export interface AdjustmentPayload {
  shopId: string
  type: 'credit' | 'debit'
  amount: number
  reason: string
}

export const billingApi = {
  listInvoices: (params: BillingListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<BillingInvoice>>>('/billing/invoices', { params }),
  listPayments: (params: BillingListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<BillingInvoice>>>('/billing/payments', { params }),
  listAdjustments: (params: BillingListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<MoneyLog>>>('/billing/adjustments', { params }),
  addManualPayment: (data: ManualPaymentPayload) =>
    api.post<ApiResponse<BillingInvoice>>('/billing/manual-payment', data),
  addAdjustment: (data: AdjustmentPayload) =>
    api.post<ApiResponse<MoneyLog>>('/billing/adjustment', data),
  getSummary: () =>
    api.get<ApiResponse<{ totalPaid: number; totalUnpaid: number; overdue: number; refunded: number; mrr: number }>>('/billing/summary'),
}

// ─── Money Logs ───────────────────────────────────────────────────────────────
export interface MoneyLogsListParams {
  page?: number
  limit?: number
  search?: string
  type?: string
}

export const moneyLogsApi = {
  list: (params: MoneyLogsListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<MoneyLog>>>('/billing/money-logs', { params }),
  get: (id: string) => api.get<ApiResponse<MoneyLog>>(`/billing/money-logs/${id}`),
}

// ─── Feature Flags ────────────────────────────────────────────────────────────
export interface FeatureFlagPayload {
  isEnabled?: boolean
  rolloutPercentage?: number
  enabledForPlans?: string[]
  internalNote?: string | null
}

export interface FlagOverridePayload {
  shopId: string
  isEnabled: boolean
  reason: string
}

export const featureFlagsApi = {
  list: () => api.get<ApiResponse<FeatureFlag[]>>('/feature-flags'),
  get: (id: string) => api.get<ApiResponse<FeatureFlag>>(`/feature-flags/${id}`),
  update: (id: string, data: FeatureFlagPayload) =>
    api.patch<ApiResponse<FeatureFlag>>(`/feature-flags/${id}`, data),
  addOverride: (id: string, data: FlagOverridePayload) =>
    api.post<ApiResponse<FeatureFlagOverride>>(`/feature-flags/${id}/override`, data),
  removeOverride: (flagId: string, shopId: string) =>
    api.delete(`/feature-flags/${flagId}/overrides/${shopId}`),
}

// ─── Menu Control ─────────────────────────────────────────────────────────────
export const menuControlApi = {
  getGlobal: () =>
    api.get<ApiResponse<MenuConfig[]>>('/menu-configs', { params: { scope: 'global' } }),
  getPlanConfigs: () =>
    api.get<ApiResponse<MenuConfig[]>>('/menu-configs', { params: { scope: 'plan' } }),
  getPlanConfig: (planId: string) =>
    api.get<ApiResponse<MenuConfig[]>>('/menu-configs', { params: { scope: 'plan', planId } }),
  getShopConfig: (shopId: string) =>
    api.get<ApiResponse<MenuConfig[]>>('/menu-configs', { params: { scope: 'shop', shopId } }),
  updateGlobal: (config: Record<string, MenuVisibility>) =>
    api.post<ApiResponse<MenuConfig>>('/menu-configs/upsert', { scope: 'global', config }),
  updatePlanConfig: (planId: string, config: Record<string, MenuVisibility>) =>
    api.post<ApiResponse<MenuConfig>>('/menu-configs/upsert', { scope: 'plan', planId, config }),
  updateShopConfig: (shopId: string, config: Record<string, MenuVisibility>) =>
    api.post<ApiResponse<MenuConfig>>('/menu-configs/upsert', { scope: 'shop', shopId, config }),
}

// ─── Notifications ────────────────────────────────────────────────────────────
export interface SendNotificationPayload {
  title: string
  body: string
  channels: string[]
  targetType: 'all' | 'specific'
  shopIds?: string[]
  scheduledAt?: string | null
}

export const notificationsApi = {
  send: (data: SendNotificationPayload) =>
    api.post<ApiResponse<NotificationLog>>('/notifications/send', data),
  listTemplates: () => api.get<ApiResponse<NotificationTemplate[]>>('/notifications/templates'),
  createTemplate: (data: Partial<NotificationTemplate>) =>
    api.post<ApiResponse<NotificationTemplate>>('/notifications/templates', data),
  updateTemplate: (id: string, data: Partial<NotificationTemplate>) =>
    api.patch<ApiResponse<NotificationTemplate>>(`/notifications/templates/${id}`, data),
  /** sent notifications, one row per message with delivery counts */
  listMessages: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<NotificationLog[]> & { total?: number }>('/notifications', { params }),
  listLogs: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<PaginatedResponse<NotificationLog>>>('/notifications/logs', { params }),
}

// ─── Support ──────────────────────────────────────────────────────────────────
export interface TicketsListParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  priority?: string
  assignedAdminId?: string
}

export const supportApi = {
  listTickets: (params: TicketsListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<SupportTicket>>>('/support/tickets', { params }),
  getTicket: (id: string) =>
    api.get<ApiResponse<SupportTicket>>(`/support/tickets/${id}`),
  replyTicket: (id: string, body: string, isInternal: boolean) =>
    api.post<ApiResponse<TicketMessage>>(`/support/tickets/${id}/messages`, { body, isInternal }),
  updateTicket: (id: string, data: Partial<SupportTicket>) =>
    api.patch<ApiResponse<SupportTicket>>(`/support/tickets/${id}`, data),
  assignTicket: (id: string, adminId: string) =>
    api.patch<ApiResponse<SupportTicket>>(`/support/tickets/${id}`, { assignedAdminId: adminId }),
  closeTicket: (id: string) =>
    api.patch<ApiResponse<SupportTicket>>(`/support/tickets/${id}`, { status: 'closed' }),
}

// ─── Admin Users ──────────────────────────────────────────────────────────────
export interface CreateAdminPayload {
  firstName: string
  lastName: string
  email: string
  password: string
  role: string
}

export const adminUsersApi = {
  list: () => api.get<ApiResponse<AdminUser[]>>('/admin-users'),
  create: (data: CreateAdminPayload) =>
    api.post<ApiResponse<AdminUser>>('/admin-users', data),
  update: (id: string, data: Partial<Pick<AdminUser, 'firstName' | 'lastName' | 'role' | 'isActive'>>) =>
    api.patch<ApiResponse<AdminUser>>(`/admin-users/${id}`, data),
  toggleActive: (id: string, isActive: boolean) =>
    api.patch<ApiResponse<AdminUser>>(`/admin-users/${id}`, { isActive }),
  resetTwoFactor: (id: string) => api.post<ApiResponse<{ id: string }>>(`/admin-users/${id}/reset-2fa`),
  resetPassword: (id: string, password: string) =>
    api.post<ApiResponse<null>>(`/admin-users/${id}/reset-password`, { password }),
  getPermissions: (id: string) => api.get<ApiResponse<UserPermissions>>(`/admin-users/${id}/permissions`),
  setPermissions: (id: string, data: { allow: string[]; deny: string[] }) =>
    api.put<ApiResponse<UserPermissions>>(`/admin-users/${id}/permissions`, data),
}

// ─── Roles & permissions ──────────────────────────────────────────────────────
export interface RolePayload {
  name?: string
  label?: string
  description?: string | null
  permissions?: string[]
  copyFrom?: string
  resetToDefault?: boolean
}

export const rolesApi = {
  catalog: () => api.get<ApiResponse<{ resources: PermissionResource[]; all: string[] }>>('/admin-roles/catalog'),
  list: () => api.get<ApiResponse<AdminRoleInfo[]>>('/admin-roles'),
  create: (data: RolePayload) => api.post<ApiResponse<AdminRoleInfo>>('/admin-roles', data),
  update: (id: string, data: RolePayload) => api.patch<ApiResponse<AdminRoleInfo>>(`/admin-roles/${id}`, data),
  remove: (id: string) => api.delete<ApiResponse<null>>(`/admin-roles/${id}`),
}

// ─── Audit Logs ───────────────────────────────────────────────────────────────
export interface AuditLogsListParams {
  page?: number
  limit?: number
  search?: string
  action?: string
  shopId?: string
  startDate?: string
  endDate?: string
  actorAdminId?: string
  targetType?: string
}

export const auditLogsApi = {
  list: (params: AuditLogsListParams = {}) =>
    api.get<ApiResponse<PaginatedResponse<AuditLog>>>('/audit-logs', { params }),
  get: (id: string) => api.get<ApiResponse<AuditLog>>(`/audit-logs/${id}`),
  actors: () => api.get<ApiResponse<{ id: string | null; name: string | null; count: number; lastAt: string }[]>>('/audit-logs/actors'),
}

// ─── System Settings ──────────────────────────────────────────────────────────
// Stored as key → string in admin.system_settings; the API speaks { settings: { key: value } }.
const SETTING_KEYS: Record<keyof Omit<SystemSettings, 'updatedAt' | 'updatedByName'>, string> = {
  maintenanceMode: 'maintenance_mode',
  signupEnabled: 'signup_enabled',
  trialDays: 'trial_days',
  defaultCurrency: 'default_currency',
  vatRate: 'vat_rate',
  supportEmail: 'support_email',
  termsUrl: 'terms_url',
  privacyUrl: 'privacy_url',
  defaultPlanId: 'default_plan',
}

export const DEFAULT_SYSTEM_SETTINGS: SystemSettings = {
  maintenanceMode: false,
  signupEnabled: true,
  trialDays: 30,
  defaultCurrency: 'THB',
  vatRate: 7,
  supportEmail: '',
  termsUrl: '',
  privacyUrl: '',
  defaultPlanId: 'starter',
  updatedAt: '',
  updatedByName: null,
}

function settingsFromApi(raw: Record<string, unknown> | undefined): SystemSettings {
  const out: SystemSettings = { ...DEFAULT_SYSTEM_SETTINGS }
  if (!raw) return out
  const o = out as unknown as Record<string, unknown>
  for (const [field, key] of Object.entries(SETTING_KEYS)) {
    const v = raw[key]
    if (v === undefined || v === null) continue
    const def = (DEFAULT_SYSTEM_SETTINGS as unknown as Record<string, unknown>)[field]
    o[field] = typeof def === 'boolean' ? v === true || v === 'true' || v === '1' : typeof def === 'number' ? Number(v) || 0 : String(v)
  }
  return out
}

export const systemSettingsApi = {
  get: () =>
    api.get<ApiResponse<Record<string, unknown>>>('/system/settings').then((r) => ({
      ...r,
      data: { ...r.data, data: settingsFromApi(r.data.data) },
    })),
  update: (data: Partial<SystemSettings>) => {
    const settings: Record<string, string> = {}
    for (const [field, key] of Object.entries(SETTING_KEYS)) {
      const v = (data as Record<string, unknown>)[field]
      if (v !== undefined) settings[key] = String(v)
    }
    return api.patch<ApiResponse<Record<string, unknown>>>('/system/settings', { settings })
  },
}

// ─── SMTP (platform email server used by queza-core) ─────────────────────────
export interface SmtpSettings {
  enabled: boolean
  host: string
  port: number
  secure: boolean
  user: string
  from: string
  hasPassword: boolean
  updatedAt: string | null
  updatedByName: string | null
}

export interface SmtpSettingsPayload {
  enabled: boolean
  host: string
  port: number
  secure: boolean
  user: string
  from: string
  /** Write-only; omit or '' to keep the stored password. */
  pass?: string
  clearPassword?: boolean
}

export const smtpSettingsApi = {
  get: () => api.get<ApiResponse<SmtpSettings>>('/system/smtp'),
  update: (data: SmtpSettingsPayload) => api.put<ApiResponse<SmtpSettings>>('/system/smtp', data),
  test: (data: Partial<SmtpSettingsPayload> & { to: string }) =>
    api.post<ApiResponse<{ sent: boolean; messageId: string | null }>>('/system/smtp/test', data),
}

// ─── System Health ────────────────────────────────────────────────────────────
export const systemHealthApi = {
  getStatus: () =>
    api.get<ApiResponse<{ services: import('../types').SystemHealthCard[]; errors: { message: string; timestamp: string; service: string; level: string }[] }>>('/system/health'),
}

// ─── Role Permissions ─────────────────────────────────────────────────────────
export type RolePermissionConfig = Record<string, { super_admin: boolean; support: boolean; finance: boolean; product_ops: boolean }>

export const rolePermissionsApi = {
  get: () => api.get<ApiResponse<RolePermissionConfig>>('/system/role-permissions'),
  update: (data: RolePermissionConfig) => api.put<ApiResponse<RolePermissionConfig>>('/system/role-permissions', data),
}

// ─── Master Data ──────────────────────────────────────────────────────────────
export interface BusinessTypePayload {
  name: string
  description?: string | null
  isActive?: boolean
  sortOrder?: number
}

export interface PaymentMethodPayload {
  type: PaymentMethodType
  label: string
  isActive?: boolean
  sortOrder?: number
  instructions?: string | null
  bankName?: string | null
  accountName?: string | null
  accountNumber?: string | null
  bankBranch?: string | null
  promptpayId?: string | null
  promptpayQr?: string | null
  cardProvider?: string | null
}

export const masterDataApi = {
  listBusinessTypes: () => api.get<ApiResponse<BusinessType[]>>('/master-data/business-types'),
  createBusinessType: (data: BusinessTypePayload) =>
    api.post<ApiResponse<BusinessType>>('/master-data/business-types', data),
  updateBusinessType: (id: string, data: Partial<BusinessTypePayload>) =>
    api.patch<ApiResponse<BusinessType>>(`/master-data/business-types/${id}`, data),
  deleteBusinessType: (id: string) => api.delete(`/master-data/business-types/${id}`),

  listPaymentMethods: () => api.get<ApiResponse<PaymentMethod[]>>('/master-data/payment-methods'),
  createPaymentMethod: (data: PaymentMethodPayload) =>
    api.post<ApiResponse<PaymentMethod>>('/master-data/payment-methods', data),
  updatePaymentMethod: (id: string, data: Partial<PaymentMethodPayload>) =>
    api.patch<ApiResponse<PaymentMethod>>(`/master-data/payment-methods/${id}`, data),
  deletePaymentMethod: (id: string) => api.delete(`/master-data/payment-methods/${id}`),
}

// Public API (no auth required)
export const publicApi = {
  getPlans: () => api.get<ApiResponse<Plan[]>>('/public/plans'),
  getBusinessTypes: () =>
    api.get<ApiResponse<Pick<BusinessType, 'id' | 'name' | 'description'>[]>>('/public/business-types'),
}

// ─── Sign-in security (2FA) ───────────────────────────────────────────────────
export interface TotpEnrollment {
  otpauthUrl: string
  qrDataUrl: string
  secret: string
}

export type LoginResult =
  | AuthResponse
  | { twoFactorRequired: true; challengeToken: string }
  | { twoFactorSetupRequired: true; challengeToken: string }

export const systemSecurityApi = {
  get: () => api.get<ApiResponse<{ require2fa: boolean; adminsTotal: number; adminsWith2fa: number }>>('/system/security'),
  update: (require2fa: boolean) => api.put<ApiResponse<{ require2fa: boolean }>>('/system/security', { require2fa }),
}

// ─── Shop payments (read-only, from queza-core) ───────────────────────────────
export interface CorePayment {
  id: string
  shopId: string
  shopName: string
  source: 'core'
  kind: 'plan' | 'addon'
  planCode: string
  billingCycle: string
  addonCode: string | null
  amount: number
  amountBaht: number
  status: 'pending' | 'verified' | 'failed'
  slipImageUrl: string | null
  slipVerificationRef: string | null
  verifiedAt: string | null
  periodStart: string | null
  periodEnd: string | null
  refundedAt?: string | null
  refundReason?: string | null
  createdAt: string
  updatedAt: string
}

export const corePaymentsApi = {
  list: (params: { page?: number; limit?: number; status?: string; kind?: string; shopId?: string } = {}) =>
    api.get<ApiResponse<CorePayment[]> & { total: number; page: number; totalPages: number }>('/reconciliation/core-payments', { params }),
  approve: (id: string, reason: string) => api.post<ApiResponse<CorePayment>>(`/reconciliation/core-payments/${id}/approve`, { reason }),
  reject: (id: string, reason: string) => api.post<ApiResponse<CorePayment>>(`/reconciliation/core-payments/${id}/reject`, { reason }),
  refund: (id: string, reason: string) => api.post<ApiResponse<CorePayment>>(`/reconciliation/core-payments/${id}/refund`, { reason }),
}

// ─── AI Usage (queza-core AiReport, per shop per month) ───────────────────────
export interface AiUsageShopRow {
  shopId: string
  shopName: string
  calls: number
  reports: number
  asks: number
  errors: number
  inputTokens: number
  outputTokens: number
  costSatang: number
  costUsd: number
  lastAt: string | null
}
export interface AiUsageSummary {
  month: string
  total: { shops: number; calls: number; reports: number; asks: number; errors: number; inputTokens: number; outputTokens: number; costSatang: number; costUsd: number }
  shops: AiUsageShopRow[]
  days: { day: string; calls: number; costSatang: number }[]
}
export interface AiUsageCall {
  id: string
  kind: string
  status: string
  model: string
  inputTokens: number
  outputTokens: number
  costSatang: number
  question: string | null
  error: string | null
  createdAt: string
}
export type CustomerAnalytics = {
  month: string
  totals: { visits: number; converted: number; onlineBookings: number; conversion: number | null; customers: number; consented: number; profiled: number; lineConnected: number }
  channels: Record<string, { visits: number; bookings: number; converted: number; conversion: number | null }>
  devices: Record<string, number>
  campaigns: { campaign: string; visits: number; bookings: number }[]
  gender: Record<string, number>
  age: Record<string, number>
  districts: { district: string; count: number }[]
  interests: { interest: string; count: number }[]
  acquisition: Record<string, number>
  shops: { shopId: string; shopName: string; visits: number; bookings: number; converted: number; conversion: number | null; customers: number; consented: number; profiled: number }[]
}
export const customerAnalyticsApi = {
  get: (month: string) => api.get<ApiResponse<CustomerAnalytics>>('/customer-analytics', { params: { month } }).then((r) => r.data.data),
}

export const aiUsageApi = {
  summary: (month: string) => api.get<ApiResponse<AiUsageSummary>>('/ai-usage', { params: { month } }),
  shop: (shopId: string, month: string) =>
    api.get<ApiResponse<{ month: string; shop: { id: string; name: string }; total: Omit<AiUsageShopRow, 'shopName'>; calls: AiUsageCall[] }>>(`/ai-usage/${shopId}`, { params: { month } }),
}

export type AnnouncementPayload = Omit<Announcement, 'id' | 'createdAt' | 'stats'>
export const announcementsApi = {
  list: () => api.get<ApiResponse<Announcement[]>>('/announcements'),
  create: (data: AnnouncementPayload) => api.post<ApiResponse<Announcement>>('/announcements', data),
  update: (id: string, data: Partial<AnnouncementPayload>) => api.patch<ApiResponse<Announcement>>(`/announcements/${id}`, data),
  resetViews: (id: string) => api.post<ApiResponse<{ removed: number }>>(`/announcements/${id}/reset-views`),
  remove: (id: string) => api.delete<ApiResponse<{ deleted: boolean }>>(`/announcements/${id}`),
}

export interface UsageAnalytics {
  days: number; since: string; shopId: string | null
  totals: { shops: number; hits: number }
  pages: { key: string; hits: number; shops: number }[]
  buttons: { key: string; hits: number; shops: number }[]
  daily: { day: string; kind: string; hits: number }[]
}
export const usageAnalyticsApi = {
  get: (days: number, shopId?: string) => api.get<ApiResponse<UsageAnalytics>>('/usage-analytics', { params: { days, ...(shopId ? { shopId } : {}) } }),
}

// ── Accounting reports ──────────────────────────────────────────────────────
export interface AccountingConfig { companyName: string; taxId: string; branchLabel: string; address: string; vatRegistered: boolean; vatRate: number; pricesIncludeVat: boolean; whtPercent: number; docPrefix: string }
export interface FinanceLedgerLine {
  id: string; docNo: string; date: string; shopId: string; shopName: string; shopEmail: string; kind: 'plan' | 'addon'; planCode: string; billingCycle: string; description: string
  periodStart: string | null; periodEnd: string | null; gross: number; discount: number; promotionCode: string; received: number; base: number; vat: number; total: number
  slipRef: string; status: 'received' | 'refunded'; refundAmount: number; refundDate: string | null; refundReason: string
}
export interface FinanceLedger { range: { from: string; to: string }; config: AccountingConfig; truncated: boolean; lines: FinanceLedgerLine[]; totals: { count: number; shops: number; gross: number; discount: number; received: number; base: number; vat: number; refunds: number; refundCount: number; netAfterRefund: number } }
export interface FinanceSalesTax { range: { from: string; to: string }; config: AccountingConfig; truncated: boolean; totals: { count: number; base: number; vat: number; total: number }; lines: { date: string; docNo: string; buyer: string; description: string; base: number; vat: number; total: number; type: 'receipt' | 'refund' }[] }
export interface FinanceMonthly { range: { from: string; to: string }; config: AccountingConfig; months: { month: string; count: number; shops: number; gross: number; discount: number; received: number; planIncome: number; addonIncome: number; base: number; vat: number; refunds: number; commissions: number; net: number }[] }
export interface FinanceCommissions { range: { from: string; to: string }; config: AccountingConfig; totals: { count: number; amount: number; wht: number; netPaid: number }; lines: { month: string; referrerId: string; code: string; name: string; type: string; bank: string; account: string; accountName: string; items: number; amount: number; wht: number; netPaid: number }[] }
export type FinanceReportKind = 'ledger' | 'sales-tax' | 'monthly' | 'commissions'
export type FinanceReportParams = { from: string; to: string; kind?: string; plan?: string; cycle?: string; status?: string; q?: string; includeTest?: string }
const cleanParams = (p: Record<string, string | undefined>) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== ''))
export const financeReportsApi = {
  config: () => api.get<ApiResponse<AccountingConfig>>('/finance-reports/config'),
  saveConfig: (data: AccountingConfig) => api.put<ApiResponse<AccountingConfig>>('/finance-reports/config', data),
  ledger: (p: FinanceReportParams) => api.get<ApiResponse<FinanceLedger>>('/finance-reports/ledger', { params: cleanParams(p) }),
  salesTax: (p: FinanceReportParams) => api.get<ApiResponse<FinanceSalesTax>>('/finance-reports/sales-tax', { params: cleanParams(p) }),
  monthly: (p: FinanceReportParams) => api.get<ApiResponse<FinanceMonthly>>('/finance-reports/monthly', { params: cleanParams(p) }),
  commissions: (p: FinanceReportParams) => api.get<ApiResponse<FinanceCommissions>>('/finance-reports/commissions', { params: cleanParams(p) }),
  /** Downloads the report as CSV (UTF-8 with BOM, opens in Excel). */
  exportCsv: async (kind: FinanceReportKind, p: FinanceReportParams) => {
    const res = await api.get<Blob>(`/finance-reports/${kind}`, { params: { ...cleanParams(p), format: 'csv' }, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `queza-${kind}_${p.from}_${p.to}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  },
}

// ── Business stats & targets ────────────────────────────────────────────────
export interface BusinessTargets { dailyRevenue: number; dailySignups: number; dailyNewPaid: number; monthlyRevenue: number; planShops: { starter: number; pro: number; business: number } }
export interface BusinessInsights {
  range: { from: string; to: string; days: number }; today: string; targets: BusinessTargets
  daily: { day: string; revenue: number; payments: number; planRevenue: number; addonRevenue: number; discount: number; starter: number; pro: number; business: number; signups: number; newPaid: number; refunds: number; weekday: number }[]
  totals: { days: number; revenue: number; payments: number; signups: number; newPaid: number; refunds: number; discount: number; planRevenue: number; addonRevenue: number; avgRevenuePerDay: number; avgPerPayment: number; daysHitRevenue: number; daysHitSignups: number; daysHitNewPaid: number }
  previous: { from: string; to: string; revenue: number; payments: number; signups: number; newPaid: number }
  weekday: { weekday: number; label: string; days: number; revenue: number; signups: number; newPaid: number; avgRevenue: number; avgSignups: number }[]
  now: { states: { plan: string; state: string; shops: number; due7: number }[]; totalShops: number; paying: number; trial: number; trialEnded: number; expired: number; unpaid: number; renewDue7: number; trialEnding7: number; payingByPlan: Record<string, number> }
  funnel: { signups: number; onboarded: number; booked: number; paid: number }
  planMix: { item: string; cycle: string; payments: number; shops: number; revenue: number }[]
  promos: { code: string; payments: number; revenue: number; discount: number }[]
  sources: { source: string; signups: number; paid: number }[]
  month: { month: string; revenue: number; target: number; daysPassed: number; daysInMonth: number; forecast: number; neededPerDay: number }
  hints: { level: 'good' | 'warn' | 'bad' | 'info'; key: string; text: string }[]
}
export const businessInsightsApi = {
  get: (from: string, to: string, includeTest = false) => api.get<ApiResponse<BusinessInsights>>('/business-insights', { params: { from, to, ...(includeTest ? { includeTest: '1' } : {}) } }),
  saveTargets: (data: BusinessTargets) => api.put<ApiResponse<BusinessTargets>>('/business-insights/targets', data),
}

// ── Integrations (core .env settings, encrypted) ────────────────────────────
export interface IntegrationField { name: string; label: string; secret: boolean; restart: boolean; help: string | null; set: boolean; value: string | null; unreadable: boolean; usedByCore: boolean; coreCannotRead: boolean }
export interface IntegrationsView { groups: { key: string; title: string; note: string | null; fields: IntegrationField[] }[]; encryptionReady: boolean; keyMatchesCore: boolean | null; coreSeenAt: string | null; updatedAt: string | null; updatedByName: string | null }
export const integrationsApi = {
  get: () => api.get<ApiResponse<IntegrationsView>>('/system/integrations'),
  update: (data: { currentPassword: string; set: Record<string, string>; clear: string[] }) => api.put<ApiResponse<IntegrationsView>>('/system/integrations', data),
}

export interface SecurityKeysView { encryptionReady: boolean; keyMatchesCore: boolean | null; coreReporting: boolean; totpPending: number | null; keys: { name: string; title: string; effect: string; managed: boolean; rotatedAt: string | null; rotatedBy: string | null; olderKeys: number; envValidUntil: string | null; coreUsing: boolean; coreCannotRead: boolean }[] }
export const securityKeysApi = {
  get: () => api.get<ApiResponse<SecurityKeysView>>('/system/security-keys'),
  rotate: (name: string, currentPassword: string) => api.post<ApiResponse<SecurityKeysView>>(`/system/security-keys/${name}/rotate`, { currentPassword }),
}
